import React from 'react';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';

const useResizableSection = (localStorageKey, args = {}) => {
  const {
    positiveDeltaMeansBigger = true, // If a positive delta between current and original position means bigger then set this to true
    isColumn = true,
    minSize = 50,
    onPickNewSize = () => {},
    defaultSizeToMin = false,
    startCollapsed = false,
  } = args;

  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const [hasMounted, setHasMounted] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(startCollapsed);

  const keys = {
    position: 'clientX',
    offset: 'offsetWidth',
    styleDimension: 'width',
  };

  if (!isColumn) {
    keys.position = 'clientY';
    keys.offset = 'offsetHeight';
    keys.styleDimension = 'height';
  }

  const sectionRef = React.useRef(null);
  const initialRef = React.useRef(null);

  const onStartDrag = (evt) => {
    // Unless both the section has mounted and we are in the middle of resizing, just quit early.
    if (!sectionRef.current) {
      return;
    }

    initialRef.current = {
      initialPosition: evt[keys.position],
      initialSize: sectionRef.current[keys.offset],
    };
  };

  const toggleCollapsed = () => {
    const nextCollapsed = !isCollapsed;
    const isCollapsing = nextCollapsed === true;
    logStandardizedEvent(categories.layout, eventTypes.resizeSection, 'toggled collapse', { eventProperties: { collapsed: nextCollapsed, localStorageKey } });

    const expandedDimension = window.localStorage.getItem(localStorageKey) || minSize;

    const prevTransition = sectionRef.current.style.transition;
    const transitionDurationMS = 250;
    const nextVisibility = isCollapsing ? 'hidden' : 'visible';
    const nextOpacity = isCollapsing ? 0 : 1;
    const nextDimension = isCollapsing ? '20px' : `${expandedDimension}px`;

    // Do transition and then reset transition on width
    sectionRef.current.style.transition = `${keys.styleDimension} ${transitionDurationMS}ms ease, opacity ${transitionDurationMS}ms ease`;
    setTimeout(() => {
      sectionRef.current.style.transition = prevTransition;
      sectionRef.current.style.visibility = nextVisibility; // Set this afterwards to avoid a weird jump in the UI
    }, transitionDurationMS);

    sectionRef.current.style.visibility = 'visible';
    sectionRef.current.style[keys.styleDimension] = nextDimension;
    sectionRef.current.style.opacity = nextOpacity;
    onPickNewSize(nextDimension);

    setIsCollapsed(nextCollapsed);
  };

  const handleDrag = (e) => {
    // Unless both the section has mounted and we are in the middle of resizing, just quit early.
    if (!(sectionRef.current && initialRef.current !== null)) {
      return null;
    }

    if (e.stopPropagation) e.stopPropagation();
    if (e.preventDefault) e.preventDefault();

    const { initialPosition, initialSize } = initialRef.current;
    const currentPosition = e[keys.position];

    const deltaSizeFromInitial = currentPosition - initialPosition;
    const updatedSize = (positiveDeltaMeansBigger) ? initialSize + deltaSizeFromInitial : initialSize - deltaSizeFromInitial;
    const updatedCurrentSectionDimension = Math.max(minSize, updatedSize, 100); // Hardcoding 100 so no matter what we don't ever make a tiny div

    sectionRef.current.style[keys.styleDimension] = `${updatedCurrentSectionDimension}px`;

    return null;
  };

  const onStopDrag = () => {
    if (initialRef.current !== null && sectionRef.current) {
      const size = sectionRef.current[keys.offset].toString();
      logStandardizedEvent(categories.layout, eventTypes.resizeSection, 'dragged to size', { eventProperties: { size, localStorageKey } });
      window.localStorage.setItem(localStorageKey, size);
      onPickNewSize(`${size}px`);
    }

    initialRef.current = null;
  };

  useComponentDidMount(() => {
    setHasMounted(true);
    document.addEventListener('mouseup', onStopDrag, { capture: true });
    document.addEventListener('mousemove', handleDrag, { capture: true });
  }, () => {
    document.removeEventListener('mouseup', onStopDrag, { capture: true });
    document.removeEventListener('mousemove', handleDrag, { capture: true });
  });

  // Set the size to local storage size
  React.useEffect(() => {
    const dimension = window.localStorage.getItem(localStorageKey);
    if (dimension && sectionRef.current) {
      sectionRef.current.style[keys.styleDimension] = `${parseInt(dimension, 10)}px`;
      onPickNewSize(sectionRef.current.style[keys.styleDimension]);
    } else if (defaultSizeToMin) {
      const newSize = `${minSize}px`;
      sectionRef.current.style[keys.styleDimension] = newSize;
      onPickNewSize(newSize);
    }
    if (startCollapsed) {
      const prevTransition = sectionRef.current.style.transition;
      sectionRef.current.style.transition = 'none';
      sectionRef.current.style.visibility = 'hidden';
      sectionRef.current.style.opacity = 0;
      sectionRef.current.style[keys.styleDimension] = '20px';
      sectionRef.current.style.transition = prevTransition;
      onPickNewSize('20px');
      setIsCollapsed(true);
    }
  }, [hasMounted, startCollapsed]);

  return {
    sectionRef,
    onStartDrag,
    onToggleCollapsed: toggleCollapsed,
    isCollapsed,
  };
};

export default useResizableSection;
